import React, { useEffect, useMemo, useState } from "react";
import { faturamentoComposicaoRevenda, faturamentoCobrancaDistribuidor, faturamentoDistribuidor, relatorioMovimentacoes } from "../services/api";
import { portalDistribuidor } from "../utils/portalModo";
import RelatorioComposicaoView from "./RelatorioComposicaoView";
import { Download, RefreshCw } from "lucide-react";

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("pt-BR");
};

function downloadCsv(filename, rows) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(";"), ...rows.map((row) => headers.map((h) => escape(row[h])).join(";"))];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const ExtratoMensal = ({ token, cobranca = false }) => {
  const now = new Date();
  const [ano, setAno] = useState(String(now.getFullYear()));
  const [mes, setMes] = useState(String(now.getMonth() + 1));
  const [loading, setLoading] = useState(false);
  const [relatorio, setRelatorio] = useState(null);
  const [movimentos, setMovimentos] = useState([]);

  const carregar = async () => {
    setLoading(true);
    try {
      const inicio = new Date(Number(ano), Number(mes) - 1, 1).toISOString();
      const fim = new Date(Number(ano), Number(mes), 0, 23, 59, 59).toISOString();

      const faturamento = cobranca
        ? faturamentoCobrancaDistribuidor(token, { ano: Number(ano), mes: Number(mes) })
        : portalDistribuidor()
          ? faturamentoDistribuidor(token, { ano: Number(ano), mes: Number(mes) })
          : faturamentoComposicaoRevenda(token, { ano: Number(ano), mes: Number(mes) });
      const [fat, mov] = await Promise.all([
        faturamento,
        relatorioMovimentacoes(token, { de: inicio, ate: fim }),
      ]);

      setRelatorio(fat?.data || null);
      setMovimentos(Array.isArray(mov?.data) ? mov.data : []);
    } catch (e) {
      console.error(e);
      setRelatorio(null);
      setMovimentos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const novosMes = useMemo(
    () => movimentos.filter((m) => m.tipo === "ativacao").length,
    [movimentos]
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {cobranca ? "Cobrança das revendas" : "Faturamento"}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-300/80">
              {cobranca
                ? "Mesmo formato do faturamento, com a tabela de preço de cada revenda."
                : portalDistribuidor()
                  ? "Faturamento na tabela da Zen, com revendas e clientes diretos. O primeiro mês da ativação fica de fora."
                  : "Composição por cliente, com a tabela de preço da revenda. O primeiro mês da ativação fica de fora."}
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={carregar} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm dark:border-white/10">
              <RefreshCw size={16} /> Atualizar
            </button>
            <button
              type="button"
              onClick={() => {
                const linhas = [relatorio?.diretas, ...(relatorio?.revendas || [])].filter(Boolean).flatMap((b) =>
                  (b.linhas || []).map((l) => ({
                    cnpj: l.cnpj,
                    razaoSocial: l.razaoSocial,
                    produto: l.produto,
                    total: l.total,
                  }))
                );
                downloadCsv(`relatorio-${ano}-${mes}.csv`, linhas);
              }}
              disabled={![relatorio?.diretas, ...(relatorio?.revendas || [])].filter(Boolean).some((b) => b.linhas?.length)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm disabled:opacity-50"
            >
              <Download size={16} /> CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <input type="number" value={ano} onChange={(e) => setAno(e.target.value)} className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" />
          <input type="number" min="1" max="12" value={mes} onChange={(e) => setMes(e.target.value)} className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" />
          <button type="button" onClick={carregar} className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm">Consultar</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <div className="zen-card rounded-xl p-4 dark:bg-white/5">
            <div className="text-sm text-gray-500">Total faturamento</div>
            <div className="text-xl font-bold">{fmtMoney(relatorio?.total)}</div>
          </div>
          <div className="zen-card rounded-xl p-4 dark:bg-white/5">
            <div className="text-sm text-gray-500">Clientes</div>
            <div className="text-xl font-bold">{relatorio?.qtdeClientes || 0}</div>
          </div>
          <div className="zen-card rounded-xl p-4 dark:bg-white/5">
            <div className="text-sm text-gray-500">Ativações no mês</div>
            <div className="text-xl font-bold">{novosMes}</div>
          </div>
        </div>

        {loading ? (
          <p>Carregando...</p>
        ) : (
          <>
            {relatorio && (
              <div className="mb-8">
                <RelatorioComposicaoView dados={relatorio} />
              </div>
            )}

            <h3 className="font-semibold mb-2">Movimentações recentes</h3>
            <div className="overflow-x-auto zen-card rounded-2xl border dark:border-white/10">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-white/5">
                  <tr>
                    <th className="text-left p-3">Data</th>
                    <th className="text-left p-3">Produto</th>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-left p-3">Módulo</th>
                  </tr>
                </thead>
                <tbody>
                  {movimentos.slice(0, 30).map((m) => (
                    <tr key={m.id} className="border-t dark:border-white/10">
                      <td className="p-3">{fmtDate(m.ocorridoEm)}</td>
                      <td className="p-3">{m.produto}</td>
                      <td className="p-3">{m.tipo}</td>
                      <td className="p-3">{m.moduloNome || "-"}</td>
                    </tr>
                  ))}
                  {!movimentos.length && (
                    <tr><td colSpan={4} className="p-6 text-center text-gray-500">Nenhuma movimentação no período.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExtratoMensal;
